import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * Spanish 0–10 marks, the official qualification bands and US letter grades.
 *
 * Sources (retrieved October 2026):
 * - Real Decreto 1125/2003, de 5 de septiembre, art. 5 (BOE-A-2003-17643,
 *   https://www.boe.es/buscar/act.php?id=BOE-A-2003-17643): marks are on a numerical scale of
 *   0 to 10 with one decimal — 0–4.9 suspenso, 5.0–6.9 aprobado, 7.0–8.9 notable, 9.0–10
 *   sobresaliente; the mention "Matrícula de Honor" may go to a mark of 9.0 or more, to at most
 *   5% of the students enrolled in a subject that year, or a single one where fewer than 20 are
 *   enrolled; and the average of the academic record is the credits obtained, each multiplied by
 *   the value of its mark, divided by the total credits obtained (art. 5.3).
 * - Two published routes to US letters:
 *   "ncsu": NC State University Study Abroad, "Europe Grade Equivalencies", Spain nota table:
 *   9–10 A; 8–8.9 A−; 7–7.9 B+; 6–6.9 B; 5.5–5.9 B−; 5.0–5.4 C; 4.9 and below F
 *   (https://studyabroad.ncsu.edu/europe-grade-equivalencies/).
 *   "ects": a two-step route. Universidad de Cantabria, "Assessment and grading scale",
 *   maps sobresaliente (9.0–10) to ECTS A, notable (7.0–8.9) to B, aprobado (5.0–6.9) to C and
 *   suspenso to FX (https://web.unican.es/en/Studying/Pages/Assessment-and-grading-scale.aspx);
 *   NC State's ECTS-to-US table on the same page then gives ECTS A = A, B = B+, C = B, D = C,
 *   E = C, FX and F = F.
 *   Both print letters only, so the grade points are the common US plus/minus values
 *   (A 4.0, A− 3.7, B+ 3.3, B 3.0, B− 2.7, C 2.0, F 0.0). A custom table is offered too.
 *
 * The official average counts only credits obtained, so a failed course (below 5) is left out
 * by default; a US evaluator may count it as an F, so that is an option.
 */

export type SpanishScale = "ncsu" | "ects" | "custom";

export const SPANISH_SCALES: readonly SpanishScale[] = [
  "ncsu",
  "ects",
  "custom",
];

export const SPANISH_MAX_MARK = 10;
export const SPANISH_PASS_MARK = 5;
/** Matrícula de Honor is open to marks of 9.0 or more. */
export const MH_MIN_MARK = 9;

export type SpanishBand = "suspenso" | "aprobado" | "notable" | "sobresaliente";

export function isValidSpanishMark(mark: number): boolean {
  return Number.isFinite(mark) && mark >= 0 && mark <= SPANISH_MAX_MARK;
}

/** The official Real Decreto 1125/2003 band for a mark; null outside 0–10. */
export function spanishBand(mark: number): SpanishBand | null {
  if (!isValidSpanishMark(mark)) {
    return null;
  }
  if (mark >= 9) {
    return "sobresaliente";
  }
  if (mark >= 7) {
    return "notable";
  }
  return mark >= 5 ? "aprobado" : "suspenso";
}

export interface SpanishLetterBand {
  min: number;
  letter: string;
  points: number;
}

const NCSU: readonly SpanishLetterBand[] = [
  { min: 9, letter: "A", points: 4 },
  { min: 8, letter: "A−", points: 3.7 },
  { min: 7, letter: "B+", points: 3.3 },
  { min: 6, letter: "B", points: 3 },
  { min: 5.5, letter: "B−", points: 2.7 },
  { min: 5, letter: "C", points: 2 },
  { min: 0, letter: "F", points: 0 },
];

/** Cantabria's sobresaliente/notable/aprobado → ECTS A/B/C, then NC State's ECTS → US. */
const ECTS_ROUTE: readonly SpanishLetterBand[] = [
  { min: 9, letter: "A", points: 4 },
  { min: 7, letter: "B+", points: 3.3 },
  { min: 5, letter: "B", points: 3 },
  { min: 0, letter: "F", points: 0 },
];

export interface CustomCutoffs {
  a: number;
  b: number;
  c: number;
}

export const DEFAULT_CUSTOM_CUTOFFS: CustomCutoffs = { a: 9, b: 7, c: 5 };

/** Cutoffs must lie in 0–10 and fall from A to C. */
export function validCustomCutoffs(cutoffs: CustomCutoffs): boolean {
  const values = [cutoffs.a, cutoffs.b, cutoffs.c];
  return (
    values.every(
      (value) => Number.isFinite(value) && value >= 0 && value <= 10
    ) &&
    cutoffs.a >= cutoffs.b &&
    cutoffs.b >= cutoffs.c
  );
}

export function spanishLetterBands(
  scale: SpanishScale,
  custom: CustomCutoffs = DEFAULT_CUSTOM_CUTOFFS
): readonly SpanishLetterBand[] {
  if (scale === "ncsu") {
    return NCSU;
  }
  if (scale === "ects") {
    return ECTS_ROUTE;
  }
  return [
    { min: custom.a, letter: "A", points: 4 },
    { min: custom.b, letter: "B", points: 3 },
    { min: custom.c, letter: "C", points: 2 },
    { min: 0, letter: "F", points: 0 },
  ];
}

export function convertSpanishMark(
  mark: number,
  bands: readonly SpanishLetterBand[]
): SpanishLetterBand | null {
  if (!isValidSpanishMark(mark)) {
    return null;
  }
  return bands.find((band) => mark >= band.min) ?? null;
}

export interface SpanishCourse {
  mark: number;
  credits: number;
  /** Matrícula de Honor; only counts for a mark of 9.0 or more. */
  mh: boolean;
}

export interface SpanishSummary {
  /** Credit-weighted average of the marks counted, out of 10. */
  average: number;
  gpa: number;
  band: SpanishBand;
  mhCount: number;
  /** Credits counted in the average. */
  creditsCounted: number;
  /** Credits of failed courses (below 5) that were left out. */
  creditsFailed: number;
}

export function summariseSpanish(
  courses: readonly SpanishCourse[],
  bands: readonly SpanishLetterBand[],
  includeFails: boolean
): SpanishSummary | null {
  let credits = 0;
  let markSum = 0;
  let pointSum = 0;
  let mhCount = 0;
  let failed = 0;
  for (const course of courses) {
    const band = convertSpanishMark(course.mark, bands);
    if (band && course.credits > 0) {
      if (course.mark < SPANISH_PASS_MARK && !includeFails) {
        failed += course.credits;
      } else {
        credits += course.credits;
        markSum += course.mark * course.credits;
        pointSum += band.points * course.credits;
        if (course.mh && course.mark >= MH_MIN_MARK) {
          mhCount += 1;
        }
      }
    }
  }
  if (credits <= 0) {
    return null;
  }
  const average = cleanFloat(markSum / credits);
  const officialBand = spanishBand(average);
  if (officialBand === null) {
    return null;
  }
  return {
    average,
    gpa: cleanFloat(pointSum / credits),
    band: officialBand,
    mhCount,
    creditsCounted: credits,
    creditsFailed: failed,
  };
}
